import { createRequestContext } from './context';
import { normalizeError, normalizePath } from './privacy';
import { emitTelemetry } from './telemetry';
import { RequestObservabilityContext } from './types';

export const CORRELATION_ID_HEADER = 'X-Correlation-ID';
export const REQUEST_ID_HEADER = 'X-Request-ID';

function requestUrl(input: RequestInfo | URL): URL {
  const value = typeof input === 'string' || input instanceof URL ? input.toString() : input.url;
  return new URL(value, globalThis.location?.origin ?? 'https://app.alphietrader.com');
}

function shouldPropagateContext(url: URL): boolean {
  const currentOrigin = globalThis.location?.origin;
  return (!currentOrigin || url.origin === currentOrigin) && url.pathname.includes('/aqt/');
}

type FetchTarget = { fetch: typeof fetch };
const installedFetchWrappers = new WeakSet<typeof fetch>();

function requestHeaders(input: RequestInfo | URL, init: RequestInit | undefined, context: RequestObservabilityContext): Headers {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));

  if (shouldPropagateContext(requestUrl(input))) {
    if (!headers.has(CORRELATION_ID_HEADER)) headers.set(CORRELATION_ID_HEADER, context.correlationId);
    if (!headers.has(REQUEST_ID_HEADER)) headers.set(REQUEST_ID_HEADER, context.requestId);
  }
  return headers;
}

function elapsedMilliseconds(startedAt: number): number {
  return Math.round((performance.now() - startedAt) * 100) / 100;
}

async function fetchWithObservability(nativeFetch: typeof fetch, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const context = createRequestContext();
  const url = requestUrl(input);
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const path = normalizePath(url.pathname);
  const startedAt = performance.now();

  try {
    const response = await nativeFetch(input, { ...init, headers: requestHeaders(input, init, context) });
    emitTelemetry(
      'frontend.http.request',
      {
        durationMs: elapsedMilliseconds(startedAt),
        method,
        ok: response.ok,
        path,
        requestId: context.requestId,
        status: response.status,
      },
      context
    );
    return response;
  } catch (error) {
    emitTelemetry(
      'frontend.http.request',
      {
        durationMs: elapsedMilliseconds(startedAt),
        method,
        path,
        requestId: context.requestId,
        ...normalizeError(error),
      },
      context
    );
    throw error;
  }
}

export function initializeFetchObservability(target: FetchTarget = globalThis): () => void {
  if (installedFetchWrappers.has(target.fetch)) return () => undefined;

  const nativeFetch = target.fetch;
  const wrappedFetch: typeof fetch = (input, init) => {
    const url = requestUrl(input);
    if (!shouldPropagateContext(url)) return nativeFetch.call(target, input, init);
    return fetchWithObservability((nextInput, nextInit) => nativeFetch.call(target, nextInput, nextInit), input, init);
  };

  installedFetchWrappers.add(wrappedFetch);
  target.fetch = wrappedFetch;

  return () => {
    if (target.fetch === wrappedFetch) target.fetch = nativeFetch;
  };
}
